import type { MoveScore } from "./move-model";
import { z } from "zod/v4";

export const EvaluationDisplaySchema = z.object({
    /**
     * The normalized evaluation value, always from White's perspective.
     * Range: -10 (winning for Black) to 10 (winning for White).
     */
    value: z.number(),
    /**
     * String representation of the evaluation value.
     * For mate scores, formatted as "M{number}" or "-M{number}".
     * For centipawn scores, formatted as a decimal string.
     */
    display: z.string(),
    /**
     * Progress value for UI, representing evaluation as a percentage.
     * 0 is best for Black, 100 is best for White.
     */
    progress: z.number(),
});

export type EvaluationDisplay = z.infer<typeof EvaluationDisplaySchema>;

const defaultEvaluationDisplay: EvaluationDisplay = {
    value: 0,
    display: "0",
    progress: 50,
};

export const from = (score: MoveScore): EvaluationDisplay => {
    if (score.type === "mate") {
        // Positive mate: White is mating. Negative: Black is mating.
        const sign = score.score < 0 ? "-" : "";
        return {
            progress: score.score < 0 ? 0 : 100,
            value: score.score < 0 ? -10 : 10,
            display: `${sign}M${Math.abs(score.score)}`,
        };
    }
    if (score.type === "cp") {
        let value = score.score / 100;
        value = Math.max(-10, Math.min(10, value));
        // convert to percentage between 0 and 100 for progressbar
        const progress = ((value + 10) / 20) * 100;
        return {
            value,
            display: String(value),
            progress: Number(progress.toFixed(2)),
        };
    }

    // fallback (should not happen)
    return structuredClone(defaultEvaluationDisplay);
};

export const EvaluationDisplay = {
    from,
    default: () => structuredClone(defaultEvaluationDisplay),
};
